import React, { useState } from "react";
import axios from "axios";
import { FiTruck, FiPackage, FiCheckCircle, FiBox } from "react-icons/fi";
import { SERVER_URL } from '../constants';

const TrackingProgress = ({ progress }) => {
  const stages = [
    { label: "Order Placed", icon: <FiPackage className="text-2xl" />, color: "green" },
    { label: "Shipped", icon: <FiTruck className="text-2xl" />, color: "blue" },
    { label: "Out for Delivery", icon: <FiBox className="text-2xl" />, color: "orange" },
    { label: "Delivered", icon: <FiCheckCircle className="text-2xl" />, color: "green" },
  ];

  return (
    <div className="relative flex items-center justify-between w-full mt-8 mb-4">
      {/* Progress Line */}
      <div className="absolute h-1 bg-gray-200 w-full top-1/2 transform -translate-y-1/2 z-0" />
      <div
        className="absolute h-1 bg-blue-500 transition-all duration-500 top-1/2 transform -translate-y-1/2 z-0"
        style={{ width: `${(progress / (stages.length - 1)) * 100}%` }}
      />

      {/* Stage Icons */}
      {stages.map((stage, index) => (
        <div
          key={index}
          className={`relative flex flex-col items-center z-10 transition-all duration-300 ${
            index <= progress ? "scale-110" : "opacity-50"
          }`}
        >
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center bg-white border-2 ${
              index <= progress
                ? `border-${stage.color}-500 text-${stage.color}-500`
                : "border-gray-300 text-gray-300"
            }`}
          >
            {stage.icon}
          </div>
          <p
            className={`text-sm font-medium mt-2 ${
              index <= progress ? `text-${stage.color}-500` : "text-gray-400"
            }`}
          >
            {stage.label}
          </p>
        </div>
      ))}
    </div>
  );
};

const OrderTracking = () => {
  const [orderID, setOrderID] = useState("");
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [noOrderFound, setNoOrderFound] = useState(false);

  const handleTrackOrder = async () => {
    setLoading(true);
    setError("");
    setTrackingInfo(null);
    setNoOrderFound(false);

    try {
      const res = await axios.get(`${SERVER_URL}/api/v1/ordertrack/${orderID}`);
      const { m } = res.data;
      if (m === 'f') {
        setNoOrderFound(true);
      } else {
        if (res.data.data) {
          const order = res.data.data;
          const progress = ["Order Placed", "Shipped", "Out for Delivery", "Delivered"].indexOf(order.delivaryStatus);
          setTrackingInfo({ ...order, progress });
        } else {
          setError("Order ID not found. Please check and try again.");
        }
      }
    } catch (err) {
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=" pt-40 min-h-screen flex flex-col items-center py-12 px-4">
      {/* Header Section */}
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-5xl font-bold text-indigo-600">Track Your Order</h1>
        <p className="text-gray-600 mt-2">Enter your order ID to get real-time updates on your shipment.</p>
      </div>

      {/* Tracking Form */}
      <div className="w-full max-w-lg bg-white rounded-lg shadow-lg p-6">
        <label htmlFor="orderID" className="block text-gray-700 font-medium mb-2">
          Order ID
        </label>
        <input
          type="text"
          id="orderID"
          value={orderID}
          onChange={(e) => setOrderID(e.target.value)}
          placeholder="Enter your Order ID"
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-4"
        />
        <button
          onClick={handleTrackOrder}
          disabled={loading || !orderID}
          className={`w-full bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium ${
            loading || !orderID
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-indigo-700"
          } transition`}
        >
          {loading ? "Tracking..." : "Track Order"}
        </button>
      </div>

      {/* Error Message */}
      {error && <div className="mt-6 text-red-600 font-medium">{error}</div>}

      {/* No Order Found Message */}
      {noOrderFound && (
        <div className="mt-10 w-full max-w-lg bg-white rounded-lg shadow-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Order Not Found</h2>
          <p className="text-gray-700 mb-4">We could not find any order matching the provided Order ID. Please check the ID and try again.</p>
          <img
            src="https://media.licdn.com/dms/image/v2/C5112AQEw1fXuabCTyQ/article-inline_image-shrink_1500_2232/article-inline_image-shrink_1500_2232/0/1581099611064?e=1743033600&v=beta&t=pihFtsZyntXSCqFRDAa33r6SS_giphQF-J88AyQ2YvU"
            alt="No Order Found"
            className="mx-auto mb-4 rounded-lg"
          />
          <button
            onClick={() => setOrderID("")}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition"
          >
            Reset
          </button>
        </div>
      )}

      {/* Tracking Info Section */}
      {trackingInfo && (
        <div className="mt-10 w-full max-w-3xl bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-indigo-600 mb-4">Tracking Details</h2>
          <p className="text-gray-700 mb-2">
            <strong>Order ID:</strong> {trackingInfo._id}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Current Status:</strong> {trackingInfo.deliveryStatus}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Estimated Delivery:</strong> {trackingInfo.estimatedDelivery || "Not available"}
          </p>
          <TrackingProgress progress={trackingInfo.progress} />
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
